import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import setAuthToken from "services/modules/utils/handel_api";
import EmployeesTemplate from "templates/employees";
import {
  getEmployeesRequest,
  addEmployeeRequest,
  updateEmployeeRequest,
  deleteEmployeeRequest,
  getEmployeeDetailsRequest,
} from "services/modules/employees";
import ConfirmationModal from "components/Molecules/Models/ConfirmationModal";
import { useTranslation } from "react-i18next";

function EmployeesPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.employees.employees);
  const count = useSelector((state) => state.employees.count);
  const details = useSelector((state) => state.employees.employee);
  const load = useSelector((state) => state.employees.load);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getEmployeeDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        ...filterObj,
      },
    };
    dispatch(getEmployeesRequest(requestData));
  };

  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      phone_number: `+${data?.country_code}${data?.phone_number}`,
      status: data?.status === "active" ? t("employees.status_active") : t("employees.status_deactivated"),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("landing.menu.home"),
    },
    {
      active: !type ? false : true,
      path: "/employees",
      title: t("employees.list_title"),
    },
    type && {
      active: false,
      title:
        id
          ? t("employees.edit")
          : t("employees.new"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      !id && formik?.resetForm();
      toast.success(
        t("employees.success_msg", {
          action: id ? t("employees.btn_update") : t("employees.btn_add"),
        })
      );
      navigate("/employees");
      getList(filterObj);
    };

    const countryCode = formik?.values?.country_code?.startsWith("+")
      ? formik?.values?.country_code?.slice(1)
      : formik?.values?.country_code;
    const phoneNumberWithoutCountryCode = formik?.values?.phone_number?.slice(
      countryCode?.length
    );

    if (id) {
      const employeeData = {
        name: data.name,
        email: data.email,
        status: data.status,
        country_code: countryCode,
        phone_number: phoneNumberWithoutCountryCode,
      };
      if (data.password) {
        employeeData.password = data.password;
        employeeData.password_confirmation = data.password_confirmation;
      }
      const requestData = {
        id,
        body: {
          employee: employeeData,
        },
        action,
      };
      dispatch(updateEmployeeRequest(requestData));
    } else {
      const requestData = {
        body: {
          employee: {
            name: data.name,
            email: data.email,
            country_code: countryCode,
            phone_number: phoneNumberWithoutCountryCode,
            password: data.password,
            password_confirmation: data.password_confirmation,
          },
        },
        action,
      };
      dispatch(addEmployeeRequest(requestData));
    }
  };

  const actionsList = ({ index, data }) => {
    const isActive = data?.status === t("employees.status_active") || data?.status === "active";
    return [
      {
        condition: () => true,
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("employees.action_edit"),
      },
      {
        condition: () => isActive,
        click: () => {
          const requestData = {
            id: data.id,
            body: {
              employee: {
                status: "deactivated",
              },
            },
            action: () => {
              toast.success(
                t("employees.success_msg", { action: t("employees.btn_update") })
              );
              getList(filterObj);
            },
          };
          dispatch(updateEmployeeRequest(requestData));
        },
        text: t("employees.action_deactivate") || "Deactivate",
      },
      {
        condition: () => !isActive,
        click: () => {
          const requestData = {
            id: data.id,
            body: {
              employee: {
                status: "active",
              },
            },
            action: () => {
              toast.success(
                t("employees.success_msg", { action: t("employees.btn_update") })
              );
              getList(filterObj);
            },
          };
          dispatch(updateEmployeeRequest(requestData));
        },
        text: t("employees.action_activate") || "Activate",
      },
      {
        condition: () => true,
        click: () => {
          setSelectedRow({ rowData: data });
          setOpenDeleteModal(true);
        },
        text: t("employees.action_delete"),
      },
    ];
  };

  const handleDelete = () => {
    const action = () => {
      setOpenDeleteModal(false);
      getList(filterObj);
      toast.success(t("employees.success_msg", { action: t("employees.action_delete") }));
    };
    dispatch(deleteEmployeeRequest({ id: selectedRow?.rowData?.id, action }));
  };

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    getList({ page_number: page });
  }, []);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader
        open={
          (load?.loading && load?.type === "list") ||
          (load?.loading && load?.type === "details") ||
          (load?.loading && load?.type === "update") ||
          (load?.loading && load?.type === "add")
        }
      />
      <EmployeesTemplate
        handlePageChange={handlePageChange}
        data={mappedData}
        details={details}
        count={count}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        breadcrumbs={breadcrumbs}
        setFilterObj={setFilterObj}
        rowData={rowData}
        paginationPage={page}
        openDeleteModal={openDeleteModal}
        handleDeleteRequest={handleDelete}
        can_create={true}
        formikValues={details}
      />
      <ConfirmationModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        handleConfirmation={handleDelete}
        confirmBtnTitle={t("employees.action_delete")}
        text={[t("employees.delete_confirmation_title"), t("employees.delete_confirmation_text")]}
        color="red"
        btnColor="red"
        load={load?.loading && load?.type === "delete"}
      />
    </>
  );
}

export default EmployeesPage;
